/**
 * Ranking ballot for one round.
 *
 * A ballot must rank every candidate exactly once, because the Borda points of
 * a short ballot would not be comparable with a full one; the server rejects a
 * partial ballot, so the submit button stays disabled until the local order
 * covers the whole candidate list.
 *
 * The local order is the source of truth while the page is open: it is seeded
 * from the caller's stored ballot when there is one, otherwise from the round's
 * own ranking — so the default ballot agrees with the recommendation the group
 * is looking at — and it only follows the server again when a fetch returns.
 */

import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { ArrowDown, ArrowUp } from 'lucide-react'

import { parsePositiveInteger } from '@shared/validation'

import { AuthGate, useAuthGuard } from '../../components/layout/guards'
import { ErrorAlert, ErrorBlock, LoadingBlock } from '../../components/layout/states'
import { PageHeader } from '../../components/layout/page-header'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { EmptyState } from '../../components/ui/primitives'
import { useMyParticipant, useRoundVotes, useSharedRound, useSubmitVote } from '../../hooks/use-shared'
import { mutationErrorText } from '../../lib/mutation-error'
import { useSharedOutlet } from './shared-layout'

export function VotePage() {
  const { t } = useTranslation()
  const { shareToken, event } = useSharedOutlet()
  const round = parsePositiveInteger(useParams().round)
  const guard = useAuthGuard()
  const participantQuery = useMyParticipant(shareToken)
  const votesQuery = useRoundVotes(shareToken, round)
  const roundQuery = useSharedRound(shareToken, round)
  const submitVote = useSubmitVote(shareToken, round ?? 0)

  const [order, setOrder] = useState<string[]>([])

  const result = votesQuery.data
  const roundDetail = roundQuery.data?.round

  useEffect(() => {
    if (!result) {
      return
    }

    const own = result.myRankings
    if (own && own.length === result.breakdown.length) {
      setOrder(own)
      return
    }

    // Recommendation order first: seeding a fresh ballot with the aggregate
    // would silently push the engine's first choice down the list.
    const recommended = [
      ...(roundDetail?.primary ? [roundDetail.primary.id] : []),
      ...(roundDetail?.alternates ?? []).map((item) => item.id),
    ].filter((id) => result.breakdown.some((entry) => entry.recommendationId === id))

    setOrder(
      recommended.length === result.breakdown.length
        ? recommended
        : result.breakdown.map((entry) => entry.recommendationId),
    )
  }, [result, roundDetail])

  function move(index: number, delta: number): void {
    const target = index + delta
    if (target < 0 || target >= order.length) {
      return
    }
    const next = [...order]
    const [moved] = next.splice(index, 1)
    if (moved === undefined) {
      return
    }
    next.splice(target, 0, moved)
    setOrder(next)
    // The previous confirmation belongs to the previous order.
    submitVote.reset()
  }

  // The shared gate keeps this page on the same waiting, failure and sign-in
  // redirect branches as every other session-only route.
  if (guard.status !== 'allowed') {
    return <AuthGate guard={guard} />
  }

  const header = (
    <PageHeader
      breadcrumbs={[
        { label: t('nav.home'), to: '/' },
        { label: event.title, to: `/e/${shareToken}` },
        { label: t('recommend.title'), to: `/e/${shareToken}/recommendations` },
        { label: round ? t('vote.roundLabel', { round }) : t('vote.title') },
      ]}
      title={round ? t('vote.roundLabel', { round }) : t('vote.title')}
      description={`${t('vote.hint')} ${t('vote.defaultOrderHint')}`}
    />
  )

  if (!round) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <EmptyState title={t('common.notFound')} description={t('error.not_found')} />
      </div>
    )
  }

  if (participantQuery.isPending || votesQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <LoadingBlock label={t('common.loading')} />
      </div>
    )
  }

  if (!participantQuery.data) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <EmptyState
          title={t('vote.notJoined')}
          description={t('vote.needJoin')}
          action={
            <Button asChild className="mt-2">
              <Link to={`/e/${shareToken}/join/location`}>{t('common.join')}</Link>
            </Button>
          }
        />
      </div>
    )
  }

  if (votesQuery.isError || !result) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <ErrorBlock
          title={t('error.internal_error')}
          onRetry={() => void votesQuery.refetch()}
          retryLabel={t('common.retry')}
        />
      </div>
    )
  }

  if (event.status === 'archived') {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <Alert variant="info">
          <AlertDescription>{t('vote.archivedHint')}</AlertDescription>
        </Alert>
      </div>
    )
  }

  if (result.breakdown.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <EmptyState title={t('recommend.noRounds')} description={t('error.no_candidates')} />
      </div>
    )
  }

  const districtNames = new Map(
    result.breakdown.map((entry) => [entry.recommendationId, entry.districtName]),
  )
  const canSubmit = order.length === result.breakdown.length && !submitVote.isPending

  return (
    <div className="flex flex-col gap-4">
      {header}

      <Card>
        <CardHeader>
          <CardTitle>{t('vote.rankingTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <ol className="flex flex-col gap-2">
            {order.map((recommendationId, index) => (
              <li
                key={recommendationId}
                className="border-border flex items-center gap-2 rounded-lg border px-3 py-2"
              >
                <span className="text-muted-foreground w-5 text-sm">{index + 1}</span>
                <span className="flex-1 text-sm font-medium">
                  {districtNames.get(recommendationId) ?? t('common.unknown')}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={t('vote.moveUp')}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUp aria-hidden />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={t('vote.moveDown')}
                  disabled={index === order.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDown aria-hidden />
                </Button>
              </li>
            ))}
          </ol>

          {submitVote.isError ? (
            <ErrorAlert message={mutationErrorText(submitVote.error, t)} />
          ) : null}

          {submitVote.isSuccess ? (
            <Alert variant="success">
              <AlertDescription>{t('vote.submitted')}</AlertDescription>
            </Alert>
          ) : null}

          <Button disabled={!canSubmit} onClick={() => submitVote.mutate({ rankings: order })}>
            {submitVote.isPending ? t('vote.submitting') : t('vote.submit')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('vote.resultTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <p className="text-muted-foreground text-xs">
            {t('vote.voteCount', { count: result.voteCount })}
            {' · '}
            {t('vote.votedPeople', { count: result.votedPeopleCount })}
            {' · '}
            {t('events.unitCount', { count: result.unitCount })}
          </p>

          <ul className="flex flex-col gap-2">
            {result.breakdown.map((entry, index) => (
              <li key={entry.recommendationId} className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground w-5 text-sm">{index + 1}</span>
                <span className="text-sm font-medium">{entry.districtName}</span>
                <Badge variant="secondary">
                  {t('vote.firstChoice', { count: entry.firstChoiceCount })}
                  {entry.firstChoiceWeight !== entry.firstChoiceCount
                    ? ` · ${t('vote.firstChoiceWeight', { count: entry.firstChoiceWeight })}`
                    : null}
                </Badge>
                <Badge variant="outline">{t('vote.bordaPoints', { points: entry.bordaPoints })}</Badge>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
